<template>
  <voice-intelligence-dashboard
    :loadingTimeslots="this.$apollo.queries.timeslots.loading"
    :loadingTranscripts="this.$apollo.queries.transcripts.loading"
    :loadingAnalytics="this.$apollo.queries.transcriptAnalytics.loading"
    :transcripts="transcripts"
    :timeslots="timeslots"
    :transcriptAnalytics="transcriptAnalytics"
    @approve-query="handleApproveQuery"
    @bulk-approve-query="handleBulkApproveQuery"
    @export-selected-query="handleExportSelectedQuery"
    @flag-query="handleFlagQuery"
    @timeslot-query="handleSelectedTimeslotQuery"
    @refresh-transcripts="handleRefreshTranscripts"
    @period-shift="handlePeriodShift"
  />
</template>

<script>
import moment from 'moment';
import { ExportToCsv } from 'export-to-csv';
import VoiceIntelligenceDashboard from '@/components/analytics/voiceIntelligence/VoiceIntelligenceDashboard';
import TranscriptsQuery from '@/graphql/voiceintelligence/transcriptListQuery.gql';
import timeslotQuery from '@/graphql/voiceintelligence/timeslotListQuery.gql';
import transcriptAnalyticsQuery from '@/graphql/voiceintelligence/transcriptAnalyticsQuery.gql';
import approveTranscriptMutation from '@/graphql/voiceintelligence/approveTranscriptMutation.gql';
import flagTranscriptMutation from '@/graphql/voiceintelligence/flagTranscriptMutation.gql';

export default {
  name: 'VoiceIntelligenceContainer',
  components: { VoiceIntelligenceDashboard, ExportToCsv },
  apollo: {
    transcripts: {
      client: 'apiv2',
      query: TranscriptsQuery,
      variables() {
        return {
          timeslotid: this.selectedTimeslot,
        };
      },
      skip() {
        return this.selectedTimeslot === '0';
      },
      result({ data }) {
        if (data && data.transcripts) {
          this.transcripts = data.transcripts;
        }
      },
      fetchPolicy: 'network-only',
    },
    timeslots: {
      client: 'apiv2',
      query: timeslotQuery,
      result({ data }) {
        if (data && data.timeslots) {
          this.timeslots = data.timeslots;
        }
      },
    },
    transcriptAnalytics: {
      client: 'apiv2',
      query: transcriptAnalyticsQuery,
      variables() {
        return {
          timeslotid: this.selectedTimeslot,
          dayOffset: this.periodOffsets.Day,
          weekOffset: this.periodOffsets.Week,
          monthOffset: this.periodOffsets.Month,
          yearOffset: this.periodOffsets.Year,
        };
      },
      skip() {
        return this.selectedTimeslot === '0';
      },
      result({ data }) {
        if (data && data.transcriptAnalytics) {
          this.transcriptAnalytics = data.transcriptAnalytics;
        }
      },
      fetchPolicy: 'network-only',
    },
  },
  methods: {
    handleApproveQuery(query) {
      this.$apollo
        .mutate({
          client: 'apiv2',
          mutation: approveTranscriptMutation,
          variables: {
            query: {
              interactionId: query.id,
              approveStatus: query.approveStatus,
              approvedById: query.approvedById,
              approvedBy: query.approvedBy,
              approveReason: query.reason,
            },
          },
        })
        .then(() => {
          this.$apollo.queries.transcripts.refetch();
          this.$apollo.queries.transcriptAnalytics.refetch();
          this.$notify({
            type: 'success',
            message: 'Voice note approved for on-air use',
          });
        })
        .catch(() => {
          this.$notify({
            type: 'error',
            message: 'Failed to approve voice note for on-air use',
          });
        });
    },
    handleBulkApproveQuery(transcripts) {
      const mutations = transcripts.map(transcript => {
        const query = {
          interactionId: transcript.interactionId.id,
          approveStatus: transcript.approveStatus,
          approvedById: transcript.approvedById,
          approvedBy: transcript.approvedBy,
          approveReason: transcript.approveReason,
        };

        return this.$apollo.mutate({
          client: 'apiv2',
          mutation: approveTranscriptMutation,
          variables: {
            query,
          },
        });
      });

      Promise.all(mutations)
        .then(() => {
          this.$apollo.queries.transcripts.refetch();
          this.$apollo.queries.transcriptAnalytics.refetch();
          this.$notify({
            type: 'success',
            message: `${transcripts.length} voice notes approved`,
          });
        })
        .catch(error => {
          this.$notify({
            type: 'error',
            message: `Failed to approve one or more voice notes ${error}`,
          });
        });
    },
    handleFlagQuery(query) {
      this.$apollo
        .mutate({
          client: 'apiv2',
          mutation: flagTranscriptMutation,
          variables: {
            query: {
              interactionId: query.id,
              flagStatus: query.flagStatus,
              flaggedById: query.flaggedById,
              flaggedBy: query.flaggedBy,
              flagReason: query.reason,
            },
          },
        })
        .then(() => {
          this.$apollo.queries.transcripts.refetch();
          this.$apollo.queries.transcriptAnalytics.refetch();
          this.$notify({
            type: query.flagStatus ? 'warning' : 'success',
            message: `Voice note ${query.action} for review`,
          });
        })
        .catch(error => {
          this.$notify({
            type: 'error',
            message: `Failed to flag Transcript ${error}`,
          });
        });
    },
    handleSelectedTimeslotQuery(timeslotId) {
      this.selectedTimeslot = timeslotId;
      this.periodOffsets = { Day: 0, Week: 0, Month: 0, Year: 0 };
    },
    handleRefreshTranscripts() {
      this.periodOffsets = { Day: 0, Week: 0, Month: 0, Year: 0 };
      this.$apollo.queries.transcripts.refetch();
      this.$apollo.queries.transcriptAnalytics.refetch();
    },
    handleExportSelectedQuery(transcripts) {
      const options = {
        filename: `voice_notes_export_${moment().format('YYYYMMDDHHmmss')}`,
        fieldSeparator: ',',
        quoteStrings: '"',
        decimalSeparator: '.',
        showLabels: true,
        showTitle: false,
        useTextFile: false,
        useBom: true,
        headers: [
          '#',
          'Sender',
          'Date Created',
          'Transcript',
          'Tags',
          'Tone',
          'Score',
          'Topics',
          'Sentiment',
          'AudioUrl',
        ],
      };

      const csvExporter = new ExportToCsv(options);

      const data = transcripts.map((transcript, index) => ({
        '#': index + 1,
        Sender: transcript.sender,
        'Date Created': transcript.timestamp,
        Transcript: transcript.transcript,
        Tags: transcript.tags.join(', '),
        Tone: transcript.tone,
        Score: transcript.score,
        Topics: transcript.topics.join(', '),
        Sentiment: transcript.sentiment,
        AudioUrl: transcript.audioUrl,
      }));

      csvExporter.generateCsv(data);
    },
    handlePeriodShift({ unit, direction }) {
      if (direction === 'previous') {
        this.periodOffsets[unit] -= 1;
      } else {
        this.periodOffsets[unit] += 1;
      }
      this.$apollo.queries.transcriptAnalytics.refetch();
    },
  },
  data() {
    return {
      transcripts: [],
      timeslots: [],
      transcriptAnalytics: null,
      loadingTranscripts: false,
      loadingTimeslots: false,
      selectedTimeslot: '0',
      periodOffsets: {
        Day: 0,
        Week: 0,
        Month: 0,
        Year: 0,
      },
    };
  },
};
</script>



// WEBPACK FOOTER //
// VoiceIntelligenceContainer.vue?b8ce377a